import { Inject, Injectable, InjectionToken } from '@angular/core';

import { AppcuesConsts, environments } from './appcues-constants';
import { IAppcuesService } from './interfaces/appcues.service.interface';
import { EnvironmentConfig } from './interfaces/environment.interface';
import {
  AppcuesCompany,
  AppcuesEventParameters,
  AppcuesUser,
  AppcuesUserParameters,
} from './interfaces/appcues-types';

export const APPCUES_CONFIG = new InjectionToken<EnvironmentConfig>(
  'APPCUES_CONFIG'
);

@Injectable({
  providedIn: 'root',
})
export class AppcuesService implements IAppcuesService {
  private _isSync: boolean = false;
  Appcues: any;
  user?: AppcuesUser;
  company?: AppcuesCompany;

  constructor(
    @Inject(APPCUES_CONFIG) private readonly _config: EnvironmentConfig
  ) {
    if (this._shouldUpload()) {
      (window as any).AppcuesSettings = { enableURLDetection: true };
      const appcueElement = document.createElement('script');
      appcueElement.src = this._config.appcuesUrl;
      appcueElement.async = true;
      const s: any = document.getElementsByTagName('script')[0];
      if (s?.parentNode) {
        s.parentNode.insertBefore(appcueElement, s);
        appcueElement.onload = () => {
          if (!this._isSync) {
            this._connectWithAppcues();
          }
          this._isSync = true;
        };
      }
    }
  }

  private readonly _shouldUpload = () => {
    return [
      environments.PRODUCTION,
      environments.STAGING,
      environments.QA,
    ].includes(this._config.ENV);
  };

  private readonly _connectWithAppcues = async () => {
    await this._isConnected();
    if (this.user && this._shouldUpload()) {
      const environment = this._config.ENV;
      this.Appcues?.identify(this.user.id, {
        environment,
        userName: this.user.usrName,
        email: this.user.eml,
      });
      if (this.company?.id) {
        this.Appcues?.group(this.company.id, {
          environment,
          companyName: this.company.name,
        });
      }
    }
    return true;
  };

  private readonly _isConnected = async (retryAttempt: number = 1) => {
    if (!this.Appcues && this._shouldUpload()) {
      if (!(<any>window).Appcues && retryAttempt > AppcuesConsts.maxAttempts) {
        throw new Error('Appcues library not found, check if it was loaded');
      } else if (
        !(<any>window).Appcues &&
        retryAttempt <= AppcuesConsts.maxAttempts
      ) {
        await new Promise((resolve) =>
          setTimeout(resolve, AppcuesConsts.reConnectWaitTime)
        );
        const attempt = retryAttempt + 1;
        await this._isConnected(attempt);
      } else {
        this.Appcues = (<any>window).Appcues;
      }
    }
    return true;
  };

  connect = async (user: AppcuesUser, company?: AppcuesCompany) => {
    this.user = user;
    this.company = company;
    if (this._isSync && this._shouldUpload()) {
      this._connectWithAppcues();
    }
    return true;
  };

  sendEvent = async (eventName: string, params: AppcuesEventParameters) => {
    if (this._shouldUpload()) {
      await this._isConnected();
      this.Appcues?.track(eventName, {
        ...(params?.description && { ...params }),
        environment: this._config.ENV,
      });
    }
    return true;
  };

  updateUser = async (params: AppcuesUserParameters = {}) => {
    await this._isConnected();
    if (this.user && this._shouldUpload() && Object.keys(params).length) {
      this.Appcues?.identify(this.user.id, {
        environment: this._config.ENV,
        ...params,
      });
    }
    return true;
  };

  page = async () => {
    if (this._shouldUpload()) {
      await this._isConnected();
      this.Appcues?.page();
    }
    return true;
  };

  logout = async () => {
    if (this._shouldUpload()) {
      await this.sendEvent(AppcuesConsts.events.logout, {
        description: AppcuesConsts.eventDescriptions.logout,
      });
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return true;
  };
}
